import type { ErrorEvent, StackFrame } from "@sentry/browser";

import { isUnhandled } from "./sentry_event";

// Tag-manager container origins, including containers a partner proxies through
// their own host while keeping the loader's filename. A container runs with full
// DOM access on the booking domain and injects tags we neither author nor
// review, so a tag that throws is a container-config bug with nothing for us to
// fix here. A container served under Google Tag first-party mode keeps none of
// these markers and is listed separately below.
const TAG_MANAGER_ORIGIN = /googletagmanager\.com|\/gtm\.js\b|\/gtag\/js\b/i;

// Containers served under Google Tag first-party mode. The mode fetches the
// container from the partner's own booking domain under a measurement path they
// choose, so the URL carries no Google marker at all — not the host, not
// `gtm.js`, not `gtag/js` — and TAG_MANAGER_ORIGIN above cannot see it. Every
// tag that throws inside such a container therefore arrives looking like our own
// same-origin code.
//
// The path is the partner's to pick and nothing we configure records it — the
// tag-manager wrapper takes a container id and never a URL — so a booking domain
// that turns the mode on has to be named here. Host and path are anchored
// together: the path alone would disown a same-named route on another domain,
// and a shape-based match on "some opaque same-origin script" would eventually
// disown one of our own. A domain nobody adds here is not silently unprotected:
// the vendor-global drop below still covers the failure these containers
// actually produce.
const FIRST_PARTY_CONTAINER_ORIGIN =
  /\/\/book\.work\.life\/metrics(?:[/?#]|$)/i;

// HubSpot's tracking loader fans out to a family of sibling script hosts, each
// shipping its own module. Matching the family rather than the single module
// that crashed keeps the next sibling's failure from arriving as a fresh,
// unowned issue. Hosts alone suffice here, unlike the tag managers above: these
// modules are only ever served from HubSpot's own CDN, never proxied through the
// partner's domain.
//
// js.hsforms.net is deliberately absent. That host serves the forms embed
// `hubspot_lets_talk_form` injects itself, and the `hbspt.forms.create` call it
// throws from takes our own portalId, formId and target — a stale formId, or an
// unmount that removes #hubspotForm before the async script loads, is our bug
// surfacing inside their frame, and Sentry is the only place it shows up.
const HUBSPOT_SCRIPT_ORIGIN =
  /hs-scripts\.com|hs-analytics\.net|hs-banner\.com|hscollectedforms\.net|hsleadflows\.net|hscta\.net|usemessages\.com/i;

// Session-recorder origin — vendor code we install but do not author. The
// recorder registers its own history listeners, so when one throws the only
// first-party frame above it is the SDK's listener wrapper. Matched on the host
// segment rather than on the vendor's name appearing anywhere in the URL, so
// that neither a lookalike domain nor an unrelated host serving a similarly
// named file can disown itself.
const SESSION_RECORDER_ORIGIN = /\/\/(?:[^/]+\.)?hotjar\.com[:/]/i;

// Visitor-identification tag, injected by a partner's container rather than by
// us. Two hosts, because the vendor serves the loader a container injects from
// one and the tag that loader in turn injects from the other, so either can be
// the frame that throws. The tag wraps its own work in an error reporter that
// reports over the network as well, so a client blocking the vendor fails the
// entitlements fetch and then the POST that tries to log that failure, leaving
// an unhandled rejection whose every frame is the vendor's. There is no
// first-party call site to disown, unlike the forms embed above. Host-anchored
// for the same reason as the recorder.
const VISITOR_IDENTIFICATION_ORIGIN =
  /\/\/(?:[^/]+\.)?(?:zi-scripts\.com|zoominfo\.com)[:/]/i;

// A browser extension's own scripts. Unlike every family above, nobody
// configured this one: an extension injects into every page the person visits,
// runs with full DOM access, and when it throws there is no partner setting to
// correct and no code of ours to reach. Only the person who installed it can
// act on it, and they never see the report.
//
// Two shapes, because an extension is not always served over a scheme. Chrome,
// Firefox and Safari's web-extension builds use one; a Safari extension bundled
// inside a macOS host app is loaded from disk and reported as a bare absolute
// path, naming either the `.appex` app-extension bundle or the legacy
// `.safariextension` resource folder. PARTNERS-WEB-6JQ is the second kind —
// every frame sits inside PayPal Honey's bundle under /Applications, with no
// scheme anywhere on the stack.
//
// The schemes are anchored and the path markers are not: a scheme is the whole
// start of a frame's URL, while the bundle path arrives mid-path. Anchoring the
// schemes also keeps one of our own asset URLs from disowning itself by merely
// carrying the text in a query string.
const BROWSER_EXTENSION_ORIGIN =
  /^(?:chrome|moz|safari-web|safari|ms-browser)-extension:\/\/|\.appex\/|\.safariextension\//i;

// A hashed bundle file we emitted. Matched on the asset path shape rather than a
// host, because the asset base is deployment-configured: it is a per-release CDN
// prefix when set and the booking domain itself otherwise, and a filter keyed to
// one of those shapes would silently disown our own frames under the other.
// beforeSend runs in the browser, before symbolication, so frame filenames are
// still the deployed asset URLs rather than the mapped source paths.
const FIRST_PARTY_BUNDLE = /\/assets\/[^/]+-[A-Za-z0-9_-]{8,}\.(?:m?js|css)\b/i;

function frameSource(frame: StackFrame): string {
  return frame.filename ?? frame.abs_path ?? "";
}

// One exception's frames can be split across several values (a cause chain), and
// both tests below judge a stack as a whole, so they share one flattening rather
// than each keeping its own view of how Sentry nests frames.
function framesOf(event: ErrorEvent): StackFrame[] {
  return (
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? []
    ) ?? []
  );
}

// The frame that attributes blame. Sentry orders frames oldest call first, so
// the last one is where the throw happened.
//
// Two of the drops below blame this frame rather than weighing the whole stack,
// because merely finding a vendor's frame proves nothing: the SDK wraps our own
// listeners and timers, so our bundle sits on the stack of anything a vendor
// script triggers. Takes the flattened stack rather than the event, so a caller
// that also needs the whole stack pays for one flattening.
function throwingFrameOf(frames: StackFrame[]): StackFrame | undefined {
  return frames[frames.length - 1];
}

// Whether the stack disowns itself entirely — no frame of ours anywhere on it.
//
// Two of the drops below weigh the whole stack this way rather than blaming the
// throwing frame, because the failures they cover carry no first-party frame at
// all when they are genuinely someone else's. Both also refuse to judge a stack
// of no frames: with nothing to disown, a drop keyed on anything else would
// silently swallow one of our own faults wearing the same message or arriving in
// the same browser, which is the one failure these filters must never have.
function hasNoFirstPartyFrame(event: ErrorEvent): boolean {
  const frames = framesOf(event);
  if (frames.length === 0) {
    return false;
  }

  return !frames.some((frame) => FIRST_PARTY_BUNDLE.test(frameSource(frame)));
}

// Scripts a social app injects into every document its in-app browser opens, on
// the platform where they carry a URL of their own: a private scheme belonging
// to the app, which no first-party asset is ever served from. The app's other
// platform attributes the same kind of script to the document instead, leaving
// no origin to name — that variant is covered by the in-app browser drop below.
const IN_APP_BROWSER_SCRIPT_ORIGIN = /^iabjs:\/\//i;

// The payment SDK's own origin. It is loaded from the vendor's CDN rather than
// bundled with us, so nothing on a stack raised inside it is ours to recognise.
//
// Deliberately absent from THIRD_PARTY_ORIGINS below, and it has to stay absent.
// Every family in that list is a marketing or analytics tag whose failures are
// none of our business, so disowning a whole origin costs nothing. This one sits
// in the checkout: a refused card confirmation throws inside this same bundle,
// with no frame of ours beneath it, so listing the origin there would discard
// the report of a booking that died along with the detached-frame noise below.
// The single drop that keys on this origin is narrowed by message instead.
const PAYMENT_SDK_ORIGIN = /\/\/js\.stripe\.com[:/]/i;

// Each vendor family gets one entry, so covering the next one is a line here
// rather than another clause threaded through the test below.
const THIRD_PARTY_ORIGINS = [
  TAG_MANAGER_ORIGIN,
  FIRST_PARTY_CONTAINER_ORIGIN,
  HUBSPOT_SCRIPT_ORIGIN,
  SESSION_RECORDER_ORIGIN,
  VISITOR_IDENTIFICATION_ORIGIN,
  BROWSER_EXTENSION_ORIGIN,
  IN_APP_BROWSER_SCRIPT_ORIGIN,
];

function isThirdPartyTagSource(source: string): boolean {
  return THIRD_PARTY_ORIGINS.some((origin) => origin.test(source));
}

// Whether a script we neither author nor review — a marketing or analytics tag,
// a browser extension, or one a host app injects — rather than our own code,
// threw. This walks the frames instead of leaning on denyUrls or
// ignoreErrors: denyUrls tests only the last frame, which for a container tag is
// the injected script attributed to the page URL rather than to the container,
// and the message ignoreErrors would need to match is browser-specific and often
// names nothing at all. Sentry's own third-party filter is no help either — it
// classifies frames by an application key injected into our bundle, and the SDK
// is bundled alongside our code, so a stack it wraps is never exclusively
// third-party.
export function isThirdPartyTagError(event: ErrorEvent): boolean {
  const frames = framesOf(event);

  const throwingFrame = throwingFrameOf(frames);
  if (!throwingFrame || FIRST_PARTY_BUNDLE.test(frameSource(throwingFrame))) {
    return false;
  }

  return frames.some((frame) => isThirdPartyTagSource(frameSource(frame)));
}

// Globals a marketing loader defines. A container tag calling one of these
// before its loader ran, or after the client blocked it, throws a bare
// ReferenceError from inside the container.
//
// Membership here is not a claim that the global is never ours: several are
// installed by wrappers we bundle — react-facebook-pixel declares `window.fbq`
// and then calls the bare identifier, lib/analytics/hubspot.ts creates
// `window._hsq`, react-hotjar creates `window.hj`. What keeps our own
// init-order bugs reportable is the first-party frame check below, not this
// list — which is also why that check refuses to judge a stack it cannot see.
const VENDOR_GLOBALS = "fbq|_fbq|ttq|snaptr|twq|lintrk|clarity|_hsq|hj";

// Matched on the message, unlike every family above, because a container served
// first-party throws from a path the partner chose: there is no origin to key on
// until we learn each one, and keying on origin alone means the next partner to
// enable the mode arrives as a fresh unowned issue.
//
// Anchored whole, for the reason the bare-fetch drop is: "fbq is not defined" is
// otherwise a substring of a message naming a wider failure. Both engine
// wordings are covered together, since matching one leaves the others free to
// page us, which then reads as a browser-specific fault rather than a blocked
// tag. The list is interpolated per wording rather than backreferenced: an
// unmatched backreference matches the empty string, so the second wording would
// silently stop matching anything.
const MISSING_VENDOR_GLOBAL = new RegExp(
  `^(?:(?:${VENDOR_GLOBALS}) is not defined|Can't find variable: (?:${VENDOR_GLOBALS}))$`
);

// Whether a marketing tag reached for its own vendor global and found nothing.
// Deliberately kept off isThirdPartyTagError's origin list: the whole point is
// to cover the containers whose URL we have no way to recognise yet, so this
// asks nothing of the stack beyond the disowning check below.
export function isMissingVendorGlobalError(event: ErrorEvent): boolean {
  // The message decides first, so the common event never pays for a frame walk.
  const namesAVendorGlobal = event.exception?.values?.some(
    (value) =>
      value.type === "ReferenceError" &&
      MISSING_VENDOR_GLOBAL.test(value.value ?? "")
  );
  if (!namesAVendorGlobal) {
    return false;
  }

  // Our bundle anywhere on the stack means one of our own call sites reached for
  // the global — our bug to fix, not a blocked tag to drop. The wrappers named
  // above call these globals bare from inside our bundle, so this is the test
  // that keeps an init-order bug of ours reportable.
  return hasNoFirstPartyFrame(event);
}

// User-agent tokens the social apps append when they render a page inside their
// own in-app browser rather than handing it to the device's. Read off the agent
// string rather than a parsed browser name because the parse happens
// server-side, after the event is accepted — at beforeSend time there is no
// browser context to read. These tokens are what that parser keys on in turn, so
// matching them classifies exactly what the issue list later shows.
//
// Each app's platforms are covered together: the token differs per platform, and
// matching one leaves the other free to report, which then reads as a fault
// specific to a phone rather than to the app.
const IN_APP_BROWSER_AGENT = /\b(?:FBAN|FBAV|FB_IAB|Instagram)\b/;

/**
 * Whether a host app's own injected script, rather than our code, threw inside
 * its in-app browser.
 *
 * These apps inject a script into every document they open — a login-field
 * scanner, a navigation timing reporter — which then runs with full DOM access
 * on the booking domain. Where that script has a URL of its own it is disowned
 * by origin like any vendor's, above. This covers the platform where it has
 * none: the frames are attributed to the document itself, so there is no origin
 * to list, no frame URL for denyUrls to test, and a message that changes with
 * each app release for ignoreErrors to chase.
 *
 * That leaves the browser as the only thing to key on, and inside that fence
 * this is the shape-based same-origin disown the first-party container note
 * above warns against. Two things hold it in: the report must be one nobody
 * caught, and no frame on it may be ours. The residual exposure is an unhandled
 * fault of ours whose every frame is inline, unhashed or nameless — worth
 * accepting, because the same fault still reports from every ordinary browser,
 * so it costs attribution for one slice of traffic rather than the signal.
 */
export function isInAppBrowserInjectedError(event: ErrorEvent): boolean {
  if (!IN_APP_BROWSER_AGENT.test(navigator.userAgent)) {
    return false;
  }

  return isUnhandled(event) && hasNoFirstPartyFrame(event);
}

// The wording the payment SDK refuses with when it tries to reach one of its
// hidden iframes and the frame it holds has already been detached from the
// document. Matched whole, like the vendor-global drop above: this is a complete
// sentence the SDK raises on its own mount invariant, so a substring test buys
// nothing and would eventually swallow a longer message describing something
// wider.
const DETACHED_PAYMENT_FRAME_MESSAGE = "frame window is not ready";

/**
 * Whether the payment SDK's postMessage bridge fired at an iframe the document
 * no longer holds.
 *
 * The SDK mounts a hidden passive-captcha frame alongside the payment element
 * and schedules a timer to refresh that frame's token shortly before it expires.
 * Navigating away tears the frame down without cancelling the timer, so the
 * timer eventually fires into a frame whose contentWindow has gone.
 *
 * There is nothing here for us to act on and nobody sees it. The throw happens
 * inside a setTimeout callback, so it reaches the browser's global handler
 * directly rather than rejecting any promise the checkout awaits — it cannot
 * fail a payment or blank an element. The timer also outlives the purchase: a
 * confirmation page left open long enough will raise this hours after its order
 * was paid.
 *
 * Keyed on the message rather than on the origin it came from, for the reason
 * PAYMENT_SDK_ORIGIN gives above. The message alone is not enough, though: the
 * SDK raises this wording from the one bridge every Elements call crosses, not
 * from the timer alone, so a confirmation aimed at a torn-down frame carries the
 * same sentence over the same frames. The unhandled test below is what separates
 * the two.
 */
export function isDetachedPaymentFrameError(event: ErrorEvent): boolean {
  // The message decides first, so the common event never pays for a frame walk.
  const namesADetachedFrame = event.exception?.values?.some(
    (value) => value.value === DETACHED_PAYMENT_FRAME_MESSAGE
  );
  if (!namesADetachedFrame) {
    return false;
  }

  // Load-bearing, not belt-and-braces: a bridge torn down mid-confirm raises
  // this very message over these very frames, and that one a booker does see.
  // Every confirmation the checkout makes runs inside a catch of its own, so
  // nothing on that path can reach the browser's global handler — anything
  // Sentry does see from it is a deliberate capture, which is a report we asked
  // for. Being uncaught is therefore the only marker separating the timer from a
  // payment that died.
  if (!isUnhandled(event)) {
    return false;
  }

  // Blame the throwing frame rather than weighing the whole stack: Sentry's
  // setTimeout wrapper is bundled alongside our code, so our own bundle always
  // sits at the foot of this stack and hasNoFirstPartyFrame would refuse to
  // disown it. Dropping that wrapper would restore honest attribution for every
  // vendor timer, but it is a default integration carrying context for all of
  // them, which is too much to trade for one drop.
  const throwingFrame = throwingFrameOf(framesOf(event));
  if (!throwingFrame) {
    return false;
  }

  return PAYMENT_SDK_ORIGIN.test(frameSource(throwingFrame));
}
